import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import {
  C5_REGISTERS,
  getSetpoints,
  requestClearFault,
  requestSetpoints,
} from "../../utils/controlConfig";
import { ProgressDialog } from "../plungerPage/progressPopup";
import axios from "../../utils/axiosInstance";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import { getUIDescription } from "../SiteAdmin/customerGroupDetails/AssigningCustomerGroupDevice";

const ALLOWED_EMAILS = [
  "SOFTCITY002@GMAIL.COM",
  "WEB@PRECISION.COM",
  "RAYMOND@OPENAIRIOT.COM",
];

export const MainCompressorCard = ({ unitId, data, fetchData }) => {
  const { pathname } = useLocation();
  const isZXUR = pathname.startsWith("/ZXUR");
  const isZWAD = pathname.startsWith("/ZWAD");
  const [form, setForm] = useState({
    allowedRPMMaxSP: "",
    loadStepControl: "2",
    speedControl: "2",
    compressorFlowRate: "2",
    propValveControl: "2",
    valvePosition: "2",
    minOutput: "",
    maxOutput: "",
    manualSP: "",
  });
  const [progressOpen, setProgressOpen] = useState(false);
  const [progressSuccess, setProgressSuccess] = useState(false);
  const [progressError, setProgressError] = useState("");
  const [progressMessage, setProgressMessage] = useState(
    "Check Device Response. Operation in-progress ...",
  );
  const [activeAction, setActiveAction] = useState(null); // 'RPM' or 'ENGINE'
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [pinError, setPinError] = useState("");
  const [pendingCmd, setPendingCmd] = useState(null);
  const [userEmail, setUserEmail] = useState(null);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const fetchMainCompressor = async () => {
    if (!unitId) return;
    try {
      if (data) {
        setForm({
          allowedRPMMaxSP: data._41023 ?? "",
          loadStepControl: data._41649 ?? "2",
          speedControl: data._41035 ?? "2",
          compressorFlowRate: data._41031 ?? "2",
          propValveControl: data._41042 ?? "2",
          valvePosition: data._41553 ?? "2",
          minOutput: parseFloat(data._41047) / 100 ?? "",
          maxOutput: parseFloat(data._41048) / 100 ?? "",
          manualSP: parseFloat(data._41049) / 100 ?? "",
        });
      }
    } catch (err) {
      console.error("Failed to get main compressor:", err);
    }
  };

  useEffect(() => {
    if (unitId) {
      fetchMainCompressor();
    }
  }, []);

  const validateFields = () => {
    const emptyFields = Object.entries(form).filter(
      ([key, value]) => value === "" || value === null || value === undefined,
    );
    return emptyFields.map(([key]) => key);
  };

  const updateMainCompressorPIDControls = async () => {
    try {
      const initialResp = await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/device_get_by_register/${unitId}`,
        C5_REGISTERS,
      );
      const baseline = {
        time: Number(initialResp.data.data?.time || 0),
        response: initialResp.data.data?.response || {},
      };
      console.log("Initial baseline time", baseline);
      const sp = (label) => parseFloat(form[label]) || 0;
      let cmd = "S";
      cmd += `,${1648},${sp("loadStepControl")}`;
      cmd += `,${1034},${sp("speedControl")}`;
      cmd += `,${1030},${sp("compressorFlowRate")}`;
      console.log(cmd);
      setProgressOpen(true);
      setProgressSuccess(false);
      await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/control/precision/cmd`,
        {
          unitId,
          cmd,
          UI_Description: `${getUIDescription()}\n Send Command to the controller \nCmd: ${cmd}`,
        },
        {
          headers: {
            id: "anything",
            "Content-Type": "application/json",
          },
        },
      );
      console.log("Sending command:", cmd);
      setTimeout(() => {
        requestSetpoints(
          unitId,
          "P,1648,1,1034,1,1030,1",
          baseline,
          setProgressSuccess,
          setProgressError,
          setProgressOpen,
        );
      }, 100);
    } catch (error) {
      console.log("Error to update main compressor PID controls", error);
    }
  };

  const updateMainCompressorValveControls = async () => {
    const missingFields = validateFields();
    if (missingFields.length > 0) {
      toast.error(
        `Please fill in the following fields: ${missingFields.join(", ")}`,
        {
          autoClose: 5000,
        },
      );
      return;
    }
    try {
      const initialResp = await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/device_get_by_register/${unitId}`,
        C5_REGISTERS,
      );
      const baseline = {
        time: Number(initialResp.data.data?.time || 0),
        response: initialResp.data.data?.response || {},
      };
      console.log("Initial baseline time", baseline);
      const sp = (label) => parseFloat(form[label]) || 0;
      let cmd = "S";
      cmd += `,${1041},${sp("propValveControl")}`;
      cmd += `,${1552},${sp("valvePosition")}`;
      cmd += `,${1048},${sp("manualSP") * 100}`;
      cmd += `,${1046},${sp("minOutput") * 100}`;
      cmd += `,${1047},${sp("maxOutput") * 100}`;
      console.log(cmd);
      setProgressOpen(true);
      setProgressSuccess(false);
      await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/control/precision/cmd`,
        {
          unitId,
          cmd,
          UI_Description: `${getUIDescription()}\n Send Command to the controller \nCmd: ${cmd}`,
        },
        {
          headers: {
            id: "anything",
            "Content-Type": "application/json",
          },
        },
      );
      console.log("Sending command:", cmd);
      setTimeout(() => {
        requestSetpoints(
          unitId,
          "P,1041,1,1552,1,1048,1,1046,1,1047,1",
          baseline,
          setProgressSuccess,
          setProgressError,
          setProgressOpen,
        );
      }, 100);
    } catch (error) {
      console.log("Error to update main compressor PID controls", error);
    }
  };

  const handlePinLogin = async () => {
    const isRPM = activeAction === "RPM";
    const correctPin = isRPM ? "3012Max" : "0720";
    if (pin !== correctPin) {
      setPinError("Invalid PIN. Please try again.");
      return;
    }
    setPinDialogOpen(false);
    setPin("");
    setPinError("");
    if (isRPM) {
      // Execute the RPM update logic
      await executeRPMUpdate(); 
    } else {
      // Execute the Engine command logic (your existing code)
      await executeEngineCommand();
    }
  };

  const executeRPMUpdate = async () => {
    try {
      const initialResp = await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/device_get_by_register/${unitId}`,
        C5_REGISTERS,
      );
      const baseline = {
        time: Number(initialResp.data.data?.time || 0),
        response: initialResp.data.data?.response || {},
      };
      console.log("Initial baseline time", baseline);
      const sp = (label) => parseFloat(form[label]) || 0;
      let cmd = "S";
      cmd += `,${1022},${sp("allowedRPMMaxSP")}`;
      console.log(cmd);
      setProgressOpen(true);
      setProgressSuccess(false);
      setProgressMessage("Updating RPM settings...");
      await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/control/precision/cmd`,
        {
          unitId,
          cmd,
          UI_Description: `${getUIDescription()}\n Send Command to the controller \nCmd: ${cmd}`,
        },
        {
          headers: {
            id: "anything",
            "Content-Type": "application/json",
          },
        },
      );
      console.log("Sending command:", cmd);
      setTimeout(() => {
        requestSetpoints(
          unitId,
          "P,1022,1",
          baseline,
          setProgressSuccess,
          setProgressError,
          setProgressOpen,
        );
      }, 100);
    } catch (error) {
      console.error("RPM Update Error:", error);
      setProgressError("Failed to update RPM");
    }
  };

  const executeEngineCommand = async () => {
  try {
    let cmd = pendingCmd;
    setProgressOpen(true);
    setProgressError("");
    setProgressSuccess(false);
    setProgressMessage("Controller Commands Execution Ongoing ...");

    const response = await axios.post(
      `${process.env.REACT_APP_API_GATEAWAY}/iot/control/precision/cmd`,
      {
        unitId,
        cmd,
        UI_Description: `${getUIDescription()}\n Engine Command \nCmd: ${cmd}`,
      },
      { headers: { id: "anything", "Content-Type": "application/json" } }
    );

    let pCmd = cmd.replace("S", "P");
      pCmd = pCmd.replace(/(-?\d+),\s*(?:-?\d+(?:\.\d+)?|NaN)/gi, "$1,1");
      if (response.data.status === "OK") {
        // after 10 seconds → update message
        setTimeout(() => {
          setProgressMessage("Polling Device. Operation in progress ...");
          requestClearFault(
            unitId,
            pCmd,
            setProgressSuccess,
            setProgressError,
            setProgressOpen,
          );
        }, 100);
      } else {
        // If not OK — show error message
        setProgressError("No data found in this Device");
        setProgressOpen(true);
        setProgressSuccess(false);
      }
  } catch (error) {
    setProgressError("Operation failed");
    setProgressOpen(true);
    setProgressSuccess(false);
  }
};

  useEffect(() => {
    const userString = localStorage.getItem("user");
    if (userString) {
      const user = JSON.parse(userString);
      setUserEmail(user.email);
    }
  }, []);

  const canSelectAppType = ALLOWED_EMAILS.includes(userEmail);

  return (
    <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
      {isZWAD && (
      <Paper sx={{ p: 2, bgcolor: "#1e293b", borderRadius: 2, mb: 2 }}>
        <Typography sx={{ mb: 2, fontSize: 14 }}>Allowed RPM max SP</Typography>
        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            sx={{ fontSize: { xs: "10px", sm: "11px", md: "12px" } }}
            fullWidth
            onClick={() =>
              getSetpoints(
                unitId,
                "P,1022,1",
                setProgressSuccess,
                setProgressError,
                setProgressOpen,
              )
            }
          >
            Get Setpoints
          </Button>
          <Button
            variant="contained"
            color="primary"
            sx={{ fontSize: { xs: "10px", sm: "11px", md: "12px" } }}
            fullWidth
            onClick={() => {
              setActiveAction("RPM");
              const confirmed = window.confirm(
                "Warning: Consider Tail shaft pully size before submitting any changes. Reminder: ALWAYS get Setpoints prior to doing the update. Please ignore if you already did"
              );
              if (!confirmed) return;
              setPinDialogOpen(true);
            }}
          >
            Submit
          </Button>
        </Box>
        <Box key={"Allowed RPM max SP"} sx={{ paddingBlock: 0.75 }}>
          <Typography sx={{ fontSize: 13 }}>Allowed RPM max SP</Typography>
          <TextField
            variant="outlined"
            fullWidth
            size="small"
            value={form.allowedRPMMaxSP ?? ""}
            onChange={(e) =>
              handleChange("allowedRPMMaxSP", e.target.value)
            }
            InputProps={{
              sx: {
                color: "#fff",
                backgroundColor: "#1f2937",
                padding: 0.2,
                fontSize: 13,
              },
            }}
          />
        </Box>
      </Paper>
      )}
      <Paper sx={{ p: 2, bgcolor: "#1e293b", borderRadius: 2, mb: 2 }}>
        <Typography sx={{ mb: 2, fontSize: 14 }}>
          Main Compressor PID Controls
        </Typography>

        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            sx={{ fontSize: { xs: "10px", sm: "11px", md: "12px" } }}
            fullWidth
            onClick={() =>
              getSetpoints(
                unitId,
                "P,1648,1,1034,1,1030,1",
                setProgressSuccess,
                setProgressError,
                setProgressOpen,
              )
            }
          >
            Get Setpoints
          </Button>
          <Button
            variant="contained"
            color="primary"
            sx={{ fontSize: { xs: "10px", sm: "11px", md: "12px" } }}
            fullWidth
            onClick={updateMainCompressorPIDControls}
          >
            Submit
          </Button>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{ fontSize: { xs: "12px", sm: "12px", md: "13px" } }}
              >
                Load Step Control
              </Typography>
              <Select
                fullWidth
                size="small"
                value={form.loadStepControl ?? ""}
                onChange={(e) =>
                  handleChange("loadStepControl", e.target.value)
                }
                style={{ fontSize: 13, backgroundColor: "#1f2937" }}
              >
                <MenuItem disabled value="2" style={{ fontSize: 13 }}>
                  Select Option
                </MenuItem>
                <MenuItem value="1" style={{ fontSize: 13 }}>
                  Auto
                </MenuItem>
                <MenuItem value="0" style={{ fontSize: 13 }}>
                  Manual
                </MenuItem>
              </Select>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{ fontSize: { xs: "12px", sm: "12px", md: "13px" } }}
              >
                Speed Control
              </Typography>
              <Select
                fullWidth
                size="small"
                value={form.speedControl ?? ""}
                onChange={(e) => handleChange("speedControl", e.target.value)}
                sx={{ mb: 1 }}
                style={{ fontSize: 13, backgroundColor: "#1f2937" }}
              >
                <MenuItem disabled value="2" style={{ fontSize: 13 }}>
                  Select Option
                </MenuItem>
                <MenuItem value="1" style={{ fontSize: 13 }}>
                  Auto
                </MenuItem>
                <MenuItem value="0" style={{ fontSize: 13 }}>
                  Manual
                </MenuItem>
              </Select>
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Box>
              <Typography
                sx={{ fontSize: { xs: "12px", sm: "12px", md: "13px" } }}
              >
                Compressor Flow Rate
              </Typography>
              <Select
                fullWidth
                size="small"
                value={form.compressorFlowRate ?? ""}
                onChange={(e) =>
                  handleChange("compressorFlowRate", e.target.value)
                }
                sx={{ mb: 1 }}
                style={{ fontSize: 13, backgroundColor: "#1f2937" }}
              >
                <MenuItem disabled value="2" style={{ fontSize: 13 }}>
                  Select Option
                </MenuItem>
                <MenuItem value="0" style={{ fontSize: 13 }}>
                  Flow Rate
                </MenuItem>
                <MenuItem value="1" style={{ fontSize: 13 }}>
                  Flow Incremental
                </MenuItem>
              </Select>
            </Box>
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ p: 2, bgcolor: "#1e293b", borderRadius: 2, mb: 2 }}>
        <Typography sx={{ mb: 2, fontSize: 14 }}>
          Main Compressor Proportional Valve Controls
        </Typography>

        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            sx={{ fontSize: { xs: "10px", sm: "11px", md: "12px" } }}
            fullWidth
            onClick={() =>
              getSetpoints(
                unitId,
                "P,1041,1,1552,1,1048,1,1046,1,1047,1",
                setProgressSuccess,
                setProgressError,
                setProgressOpen,
              )
            }
          >
            Get Setpoints
          </Button>
          <Button
            variant="contained"
            color="primary"
            sx={{ fontSize: { xs: "10px", sm: "11px", md: "12px" } }}
            fullWidth
            onClick={updateMainCompressorValveControls}
          >
            Submit
          </Button>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 13 }}>Prop Valve Control</Typography>
              <Select
                fullWidth
                size="small"
                value={form.propValveControl ?? ""}
                onChange={(e) =>
                  handleChange("propValveControl", e.target.value)
                }
                style={{ fontSize: 13, backgroundColor: "#1f2937" }}
              >
                <MenuItem disabled value="2" style={{ fontSize: 13 }}>
                  Select Option
                </MenuItem>
                <MenuItem value="1" style={{ fontSize: 13 }}>
                  Auto
                </MenuItem>
                <MenuItem value="0" style={{ fontSize: 13 }}>
                  Manual
                </MenuItem>
              </Select>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 13 }}>Valve Position@4mA</Typography>
              <Select
                fullWidth
                size="small"
                value={form.valvePosition ?? ""}
                onChange={(e) => handleChange("valvePosition", e.target.value)}
                sx={{ mb: 1 }}
                style={{ fontSize: 13, backgroundColor: "#1f2937" }}
              >
                <MenuItem disabled value="2" style={{ fontSize: 13 }}>
                  Select Option
                </MenuItem>
                <MenuItem value="1" style={{ fontSize: 13 }}>
                  Open
                </MenuItem>
                <MenuItem value="0" style={{ fontSize: 13 }}>
                  Closed
                </MenuItem>
              </Select>
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 13 }}>Minimum Output %</Typography>
              <TextField
                size="small"
                value={form.minOutput ?? ""}
                onChange={(e) => handleChange("minOutput", e.target.value)}
                sx={{
                  mb: 1,
                  input: {
                    color: "#fff",
                    backgroundColor: "#1f2937",
                    fontSize: 13,
                  },
                }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 13 }}>Maximum Output %</Typography>
              <TextField
                size="small"
                value={form.maxOutput ?? ""}
                onChange={(e) => handleChange("maxOutput", e.target.value)}
                sx={{
                  mb: 1,
                  input: {
                    color: "#fff",
                    backgroundColor: "#1f2937",
                    fontSize: 13,
                  },
                }}
              />
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Box>
              <Typography sx={{ fontSize: 13 }}>Manual SP</Typography>
              <TextField
                size="small"
                value={form.manualSP ?? ""}
                onChange={(e) => handleChange("manualSP", e.target.value)}
                sx={{
                  mb: 1,
                  input: {
                    color: "#fff",
                    backgroundColor: "#1f2937",
                    fontSize: 13,
                  },
                }}
              />
            </Box>
          </Box>
        </Box>
      </Paper>

      {isZWAD && (
        <Paper sx={{ p: 2, bgcolor: "#1e293b", borderRadius: 2 }}>
          <Typography sx={{ mb: 2, fontSize: 14 }}>Engine Controls</Typography>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            sx={{ fontSize: 12, mb: 2 }}
            onClick={() => {
              setPendingCmd("S,93,4");
              setActiveAction("ENGINE");
              setPinDialogOpen(true);
            }}
          >
            Clear Fault
          </Button>
          {canSelectAppType && (
            <Button
              variant="contained"
              color="primary"
              fullWidth
              sx={{ fontSize: 12, mb: 2 }}
              onClick={() => {
                setPendingCmd("S,93,4,1028,1,1028,0");
                setActiveAction("ENGINE");
                setPinDialogOpen(true);
              }}
            >
              Start Engine
            </Button>
          )}

          <Button
            variant="contained"
            color="primary"
            fullWidth
            sx={{ fontSize: 12 }}
            onClick={() => {
              setPendingCmd("S,1027,1,1027,0");
              setPinDialogOpen(true);
            }}
          >
            Stop Engine
          </Button>
        </Paper>
      )}
      <ProgressDialog
        open={progressOpen}
        isSuccess={progressSuccess}
        errorMessage={progressError}
        onClose={() => {
          setProgressOpen(false);
          setProgressError("");
        }}
        onRefresh={async () => {
          setProgressOpen(false);
          setProgressError("");
          await fetchData();
        }}
        loadingMessage={progressMessage}
        successMessage="Successfully updated!"
      />
      {/* 🔒 PIN Dialog */}
      <Dialog
        open={pinDialogOpen}
        onClose={() => setPinDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 2, bgcolor: "#1e293b" } }}
      >
        <DialogTitle sx={{ fontSize: 16, color: "#fff" }}>
          Enter Access PIN
        </DialogTitle>
        <DialogContent>
          <TextField
            label="PIN"
            type="password"
            variant="outlined"
            fullWidth
            value={pin}
            onChange={(e) => {
              setPin(e.target.value);
              setPinError("");
            }}
            sx={{
              input: { color: "#fff" },
              label: { color: "#ccc" },
              mt: 1,
              mb: 1,
            }}
          />
          {pinError && (
            <Typography sx={{ color: "white", fontSize: 13 }}>
              {pinError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setPinDialogOpen(false);
              setPin("");
              setPinError("");
            }}
            color="white"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button onClick={handlePinLogin} color="primary" variant="contained">
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};
